import { useEffect, useMemo, useRef, useState } from 'react';
// @mui
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Pagination,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
// components
import { BlogFeaturedCard, BlogPostCard, BlogPostsSort, BlogPostsSearch } from '../../sections/@dashboard/blog';
import { SectionHeader } from '../../sections/web/common';
import { ContactWidget } from '../../sections/web/home';
import Iconify from '../../components/iconify';
import { Seo, collectionSchema } from '../../components/seo';
// hooks
import useResponsive from '../../hooks/useResponsive';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getBlogPosts } from '../../redux/slices/blog';
// blog (category labels only — posts come from the backend)
import { BLOG_CATEGORIES } from '../../utils/blog';

// ----------------------------------------------------------------------

const SORT_OPTIONS = [
  { value: 'latest', label: 'Latest' },
  { value: 'popular', label: 'Popular' },
  { value: 'oldest', label: 'Oldest' },
];

const POSTS_PER_PAGE = 9;
const ALL_CATEGORIES = 'All';
// Filter UI should never sit empty while the catalogue loads: the model
// choices double as stable fallback chips until real categories arrive.
const FALLBACK_CATEGORIES = ['Exam Prep', 'Licensing', 'Career Tips', 'Success Stories'];

// ----------------------------------------------------------------------

export default function BlogsPage() {
  const dispatch = useDispatch();
  const { posts, isLoading, error } = useSelector((state) => state.blog);

  const listRef = useRef(null);
  const isUpSm = useResponsive('up', 'sm');

  const [query, setQuery] = useState('');
  // Debounced search term: the backend filters (`q`) server-side, but only
  // once the user pauses typing so every keystroke is not a request.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [sortBy, setSortBy] = useState('latest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 400);

    return () => window.clearTimeout(timer);
  }, [query]);

  // Filter/search/sort state in the shape the backend expects. Memoised so the
  // fetch effect (and the retry button) always send identical params.
  const requestParams = useMemo(
    () => ({
      category: category === ALL_CATEGORIES ? undefined : category,
      query: debouncedQuery || undefined,
      sort: sortBy,
    }),
    [category, debouncedQuery, sortBy]
  );

  // Server-side filter/search/sort, client-side pagination over the full
  // filtered payload so the featured hero never duplicates a grid card.
  useEffect(() => {
    dispatch(getBlogPosts(requestParams));
  }, [dispatch, requestParams]);

  const handleRetry = () => {
    dispatch(getBlogPosts(requestParams));
  };

  const handleSearch = (value) => {
    setQuery(value);
    setPage(1);
  };

  const handleCategoryChange = (next) => {
    setCategory(next);
    setPage(1);
  };

  const handleSort = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleReset = () => {
    setQuery('');
    setDebouncedQuery('');
    setCategory(ALL_CATEGORIES);
    setSortBy('latest');
    setPage(1);
  };

  // Category chips come from the loaded catalogue; the known model choices
  // stand in until the first response arrives.
  const categories = useMemo(() => {
    const fromPosts = Array.isArray(posts)
      ? [...new Set(posts.map((post) => post?.category).filter(Boolean))]
      : [];

    if (fromPosts.length > 0) {
      return fromPosts;
    }

    return BLOG_CATEGORIES?.length ? BLOG_CATEGORIES : FALLBACK_CATEGORIES;
  }, [posts]);

  // The first post becomes a featured hero on page 1 (unless searching) and is
  // excluded from the grid so the same post never shows up twice. The debounced
  // term is the source of truth here because it is what the payload was
  // fetched with, so the hero can never duplicate a grid card.
  const featuredEnabled = !debouncedQuery && posts.length > 0;
  const gridSource = featuredEnabled ? posts.slice(1) : posts;

  const totalPages = Math.max(1, Math.ceil(gridSource.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  const showFeatured = featuredEnabled && currentPage === 1;
  const gridPosts = gridSource.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);

  // Index range of what the listing actually renders. The hero sits above the
  // grid but is excluded from it, so it only shifts the numbering once the user
  // is past page 1 (where it is no longer on screen).
  const shownBeforeGrid = (currentPage - 1) * POSTS_PER_PAGE + (featuredEnabled && currentPage > 1 ? 1 : 0);
  const firstIndex = shownBeforeGrid + 1;
  const lastIndex = shownBeforeGrid + gridPosts.length + (showFeatured ? 1 : 0);

  const hasFilters = Boolean(debouncedQuery) || category !== ALL_CATEGORIES;
  // A cold load (or a failed one) must not flash the "nothing found" card, so
  // the empty state only claims there is nothing when a response actually said so.
  const isPending = isLoading && posts.length === 0;
  const hasFailed = !isLoading && Boolean(error) && posts.length === 0;
  const hasContent = gridPosts.length > 0 || showFeatured;
  const showResults = hasContent && !isPending && !hasFailed;

  const chipSx = {
    flexShrink: 0,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <>
      <Seo
        title="Healthcare Licensing Blog & Exam Tips"
        description="Exam tips, licensing updates and success stories to help healthcare professionals prepare for and pass DHA, MOH and HAAD licensing exams."
        path="/blogs"
        jsonLd={collectionSchema({
          name: 'Healthcare Licensing Blog & Exam Tips',
          description:
            'Exam tips, licensing updates and success stories for healthcare professionals preparing for DHA, MOH and HAAD licensing exams.',
          path: '/blogs',
          // The markup tracks the loaded catalogue; a crawler always gets the
          // unfiltered list because that is what the first request returns.
          items: posts.map((post) => ({ name: post.title, path: `/blog/${post.slug}` })),
        })}
      />

      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <SectionHeader
            eyebrow="Insights & Updates"
            title="Healthcare Licensing Blog & Exam Tips"
            titleComponent="h1"
            description="Exam tips, licensing updates and success stories to help UAE healthcare professionals get licensed faster."
          />

          {/* Filter toolbar */}
          <Card
            ref={listRef}
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: { xs: 3, md: 4 },
              scrollMarginTop: { xs: 120, md: 160 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: { xs: 0.5, md: 0 },
                overflowX: { xs: 'auto', md: 'visible' },
                flexWrap: { xs: 'nowrap', md: 'wrap' },
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 3,
                  bgcolor: 'divider',
                },
              }}
            >
              <Chip
                label={ALL_CATEGORIES}
                onClick={() => handleCategoryChange(ALL_CATEGORIES)}
                color={category === ALL_CATEGORIES ? 'primary' : 'default'}
                variant={category === ALL_CATEGORIES ? 'filled' : 'outlined'}
                sx={chipSx}
              />
              {categories.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  onClick={() => handleCategoryChange(item)}
                  color={category === item ? 'primary' : 'default'}
                  variant={category === item ? 'filled' : 'outlined'}
                  sx={chipSx}
                />
              ))}
            </Box>

            <Divider sx={{ my: { xs: 2, md: 2.5 } }} />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Box sx={{ width: { xs: '100%', sm: 340 } }}>
                <BlogPostsSearch
                  value={query}
                  onChange={handleSearch}
                  placeholder="Search by title, summary or keyword..."
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}
                >
                  Sort by
                </Typography>
                <BlogPostsSort options={SORT_OPTIONS} value={sortBy} onSort={handleSort} />
              </Box>
            </Stack>
          </Card>

          {/* Results summary */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: { xs: 2.5, md: 3 }, flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              {isPending ? (
                'Loading articles…'
              ) : hasFailed ? (
                'Articles could not be loaded'
              ) : hasContent ? (
                <>
                  Showing <strong>{firstIndex}</strong>–<strong>{lastIndex}</strong> of{' '}
                  <strong>{posts.length}</strong> {posts.length === 1 ? 'article' : 'articles'}
                  {category !== ALL_CATEGORIES ? ` in ${category}` : ''}
                </>
              ) : (
                'No articles match your filters'
              )}
            </Typography>

            {hasFilters && (
              <Button
                size="small"
                variant="text"
                onClick={handleReset}
                startIcon={<Iconify icon="eva:close-fill" />}
              >
                Clear filters
              </Button>
            )}
          </Stack>

          {isPending ? (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {Array.from({ length: POSTS_PER_PAGE }, (_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rounded" height={360} sx={{ transform: 'none' }} />
                </Grid>
              ))}
            </Grid>
          ) : hasFailed ? (
            <Card sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                We could not load articles
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                Please check your connection and try again.
              </Typography>
              <Button variant="outlined" onClick={handleRetry} startIcon={<Iconify icon="eva:refresh-fill" />}>
                Try again
              </Button>
            </Card>
          ) : hasContent ? (
            <>
              {showFeatured && <BlogFeaturedCard post={posts[0]} />}

              {gridPosts.length > 0 && (
                <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
                  {gridPosts.map((post, index) => (
                    <BlogPostCard key={post.id} post={post} index={index} />
                  ))}
                </Grid>
              )}
            </>
          ) : (
            <Card sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                No posts found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                Try a different search term or category.
              </Typography>
              {hasFilters && (
                <Button variant="outlined" onClick={handleReset}>
                  Clear filters
                </Button>
              )}
            </Card>
          )}

          {showResults && totalPages > 1 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mt: { xs: 4, md: 6 }, alignItems: 'center', justifyContent: 'center' }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                siblingCount={isUpSm ? 1 : 0}
                showFirstButton={isUpSm}
                showLastButton={isUpSm}
                sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}
              />
            </Stack>
          )}

          <Box sx={{ mt: { xs: 6, md: 8 } }}>
            <ContactWidget />
          </Box>
        </Container>
      </Box>
    </>
  );
}
